import { LoadingRows } from "../../UI/Loading/LoadingRows.tsx";
import { useEffect, useMemo, useState } from "react";
import { formatDateTime } from "../../UI/Utils/formatDate.ts";
import copy from "copy-to-clipboard";
import { Dialog } from "@headlessui/react";
import { ClipboardIcon } from "@heroicons/react/24/outline/index.js";
import { FranchiseeItemFragment } from "../../gql/graphql.ts";
import { Modal } from "../../UI/Modal/Modal.tsx";
import { useAllFranchisees } from "../../Franchisee/useAllFranchisees.ts";
import { useClassRoomSelectionHistory } from "../../BookedClassRooms/useClassRoomSelectionHistory.ts";
import { ClassRoomSelectionHistory } from "../../BookedClassRooms/ClassRoomSelectionHistory.tsx";
import { ClassRoomDetailScreenPath } from "../../ClassRoom/ClassRoomDetailScreen.tsx";
import { UserClassRoomHistoryItemDto } from "./types.ts";

function DetailLine({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className={"flex items-baseline justify-between gap-3 text-sm"}>
      <span className={"text-slate-500 shrink-0"}>{label}</span>
      <span className={"text-slate-800 text-right break-all"}>{children}</span>
    </div>
  );
}

function CopyableEmail({ email }: { email: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timeout = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timeout);
  }, [copied]);

  return (
    <span className={"inline-flex items-center gap-2"}>
      <button
        onClick={() => {
          copy(email);
          setCopied(true);
        }}
        title={"Copy email"}
        className={
          "inline-flex items-center gap-1 text-primary underline hover:brightness-110"
        }
      >
        {email}
        <ClipboardIcon className={"w-3.5 h-3.5 shrink-0"} />
      </button>
      {copied && (
        <span className={"text-xs text-green-600 shrink-0"}>Copied!</span>
      )}
    </span>
  );
}

export function ClassRoomTeacherDetailModal({
  classRoom,
  onClose,
}: {
  classRoom: UserClassRoomHistoryItemDto;
  onClose: () => void;
}) {
  const { fetchHistory, history, loading } = useClassRoomSelectionHistory();
  const { franchisees } = useAllFranchisees();

  useEffect(() => {
    fetchHistory(classRoom.classRoomId);
  }, [fetchHistory, classRoom.classRoomId]);

  const teacherById = useMemo(
    () =>
      new Map<string, FranchiseeItemFragment>(
        franchisees.map((f) => [f.id, f]),
      ),
    [franchisees],
  );

  const teacher = teacherById.get(classRoom.teacherId);
  const teacherName =
    classRoom.teacherGivenName || classRoom.teacherFamilyName
      ? `${classRoom.teacherGivenName ?? ""} ${classRoom.teacherFamilyName ?? ""}`.trim()
      : classRoom.teacherId;
  const email = classRoom.teacherEmail ?? teacher?.email;
  const phone = teacher?.phoneNumber
    ? `${teacher.phoneNumber.countryCode} ${teacher.phoneNumber.number}`
    : null;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={
        <div>
          <span className={"font-bold text-lg leading-tight"}>
            {teacherName}
          </span>
          <p className={"text-xs font-normal text-slate-400"}>
            Lesson on {formatDateTime(classRoom.startUtc)}
          </p>
        </div>
      }
      /* Must be a Dialog.Panel: it is what headlessui uses to tell an outside
         click from an inside one, so a plain div would never close on click away. */
      overridePanel={({ title, children }) => (
        <Dialog.Panel
          className={
            "w-full max-w-lg transform overflow-hidden rounded-lg bg-white text-left align-middle shadow-xl transition-all"
          }
        >
          <div className={"px-4 py-3 border-b border-slate-200"}>{title}</div>
          <div className={"max-h-[70vh] overflow-y-auto"}>{children}</div>
        </Dialog.Panel>
      )}
    >
      <div className={"p-4 space-y-4"}>
        <div className={"space-y-1.5"}>
          <DetailLine label={"Email"}>
            {email ? <CopyableEmail email={email} /> : "—"}
          </DetailLine>
          {phone && <DetailLine label={"Phone"}>{phone}</DetailLine>}
          {teacher && (
            <DetailLine label={"Internal teacher"}>
              {teacher.internalTeacher ? "Yes" : "No"}
            </DetailLine>
          )}
          {teacher && (
            <DetailLine label={"Does 1 to 1"}>
              {teacher.lesson1on1 ? "Yes" : "No"}
            </DetailLine>
          )}
          <DetailLine label={"Teacher id"}>{classRoom.teacherId}</DetailLine>
        </div>

        <div className={"border-t border-slate-100 pt-3"}>
          <h3
            className={
              "font-bold text-xs uppercase tracking-wide text-slate-500"
            }
          >
            Teacher selection
          </h3>
          {loading ? (
            <div className={"pt-2"}>
              <LoadingRows rows={4} />
            </div>
          ) : !history || history.length === 0 ? (
            <p className={"text-xs text-slate-400 italic pt-2"}>
              No selection data (pre-feature classroom)
            </p>
          ) : (
            <ClassRoomSelectionHistory
              history={history}
              teacherById={teacherById}
            />
          )}
        </div>

        <div className={"border-t border-slate-100 pt-3"}>
          <a
            href={ClassRoomDetailScreenPath.replace(
              ":classRoomId",
              classRoom.classRoomId,
            )}
            target={"_blank"}
            rel={"noreferrer"}
            className={"text-xs text-primary underline hover:brightness-110"}
          >
            Details
          </a>
        </div>
      </div>
    </Modal>
  );
}
